import { FC, FormEventHandler, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { AsyncSearch, DataTable, Dialog, useDialog } from 'components';
import { downloadCsv } from 'services/download-csv';
import { fetchUnitOrders } from 'services/unit-orders';
import { Customer, UnitOrder } from 'types';
import { T_DataTableColumn } from 'types/data-table';

import ErrorDialog from 'components/organization/dialog/error-dialog';
import SuccessDialog from 'components/organization/dialog/success-dialog';
import { DataTableRef } from 'components/tables/data-table';
import { useCustomerAPI } from 'hooks/use-customer-api';
import { useOrderApi } from 'hooks/use-order-api';
import ImportOrders from './import-orders';
import './unit-order.scss';

const DeleteUnitOrder: FC<{ order: UnitOrder; handleClose: () => void }> = ({ order, handleClose }) => {
  const { deleteOrder } = useOrderApi();

  const handleSubmit = useCallback(async () => {
    await deleteOrder(order);
    handleClose();
  }, [order, deleteOrder, handleClose]);

  return (
    <div className="grid-3 auto-rows">
      <h3 className="col-span-3 font-weight-medium text-info-dark">Delete {!order.allowed ? 'Stop' : 'Allow'} Order</h3>
      <p className="col-span-3 text-big text-info-main white-space-wrap">
        Are you sure you want to delete {!order.allowed ? 'stop' : 'allow'} order for account&nbsp;
        <b>{order.customer?.name?.toUpperCase() ?? ''}</b> and unit <b>{order.unitNumber ?? ''}</b>?
      </p>
      <button className="btn-outline col-start-2 align-self-end" onClick={handleClose}>
        cancel
      </button>
      <button className="btn-danger col-start-3 align-self-end" onClick={handleSubmit}>
        delete
      </button>
    </div>
  );
};

const EditUnitOrder: FC<{ order: UnitOrder; handleClose: () => void }> = ({ handleClose, ...props }) => {
  const { editOrder } = useOrderApi();
  const { searchCustomers } = useCustomerAPI();

  const [order, setOrder] = useState<Partial<UnitOrder>>({ ...props.order });

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async e => {
      e.preventDefault();
      await editOrder(order as UnitOrder);
      handleClose();
    },
    [order, editOrder, handleClose]
  );

  return (
    <form className="grid-3 auto-rows" onReset={handleClose} onSubmit={handleSubmit} data-cy="edit-order-form">
      <h3 className="col-span-3 font-weight-medium">Edit {!order.allowed ? 'Stop' : 'Allow'} Order</h3>
      <div>
        <h6 className="text-info-dark">ACCOUNT *</h6>
        <div className="mt-2 w-100">
          <AsyncSearch<Customer>
            value={order.customer}
            formatValue={c => c?.name ?? ''}
            formatOption={({ name }) => name}
            handleSelect={customer => setOrder(p => ({ ...p, customer }))}
            search={searchCustomers}
            required
            data-cy="edit-order-account-search"
          />
        </div>
      </div>
      <div className="col-span-2 row-span-2 h-100 d-flex flex-column">
        <h6 className="text-info-dark">NOTES</h6>
        <textarea
          className="mt-2 w-100 flex-fill"
          value={order.notes ?? ''}
          onChange={e => setOrder(p => ({ ...p, notes: e.target.value.toUpperCase() }))}
          data-cy="edit-order-notes-input"
        />
      </div>
      <div>
        <h6 className="text-info-dark">UNIT NUMBER *</h6>
        <input
          className="mt-2 w-100"
          type="text"
          value={order.unitNumber ?? ''}
          onChange={e => setOrder(p => ({ ...p, unitNumber: e.target.value.toUpperCase() }))}
          required
          data-cy="edit-order-unit-number-input"
        />
      </div>
      <button className="btn-outline col-start-2 align-self-end" type="reset">
        close
      </button>
      <button className="btn-primary col-start-3 align-self-end" type="submit">
        submit
      </button>
    </form>
  );
};

interface OrderColumnType {
  id: T_DataTableColumn<UnitOrder, 'number'>;
  allowed: T_DataTableColumn<UnitOrder, 'boolean'>;
  createdBy: T_DataTableColumn<UnitOrder, 'text'>;
  createdDate: T_DataTableColumn<UnitOrder, 'dateTime'>;
  customer: T_DataTableColumn<UnitOrder, 'customer'>;
  lastUpdatedBy: T_DataTableColumn<UnitOrder, 'text'>;
  lastUpdatedDate: T_DataTableColumn<UnitOrder, 'dateTime'>;
  notes: T_DataTableColumn<UnitOrder, 'text'>;
  unitNumber: T_DataTableColumn<UnitOrder, 'text'>;
}

const unitOrderColumns: { [P in keyof OrderColumnType]: OrderColumnType[P] }[keyof OrderColumnType][] = [
  { width: 80, field: 'id', label: 'ID', type: 'number' },
  { width: 80, field: 'createdDate', label: 'DATE', type: 'dateTime', dateFormat: 'LL' },
  { width: 80, field: 'createdBy', label: 'USER', type: 'text' },
  {
    width: 80,
    field: 'allowed',
    label: 'TYPE',
    type: 'boolean',
    getValueLabel: allow => (!allow ? 'STOP' : 'ALLOW'),
    skipQuickSearch: true /* boolean true seems match any quick search, results become useless */
  },
  { width: 80, field: 'customer', label: 'ACCOUNT', type: 'customer', getValueLabel: customer => customer?.name },
  { width: 80, field: 'unitNumber', label: 'UNIT', type: 'text' },
  { width: 80, field: 'notes', label: 'NOTES', type: 'text' }
];

const UnitOrders: FC = () => {
  const navigate = useNavigate();
  const { closeDialog, openDialog, ref } = useDialog();
  const [deleteOrder, setDeleteOrder] = useState<UnitOrder>();
  const [editOrder, setEditOrder] = useState<UnitOrder>();
  const [viewOrder, setViewOrder] = useState<UnitOrder>();
  const [importOpen, setImportOpen] = useState<boolean>(false);

  const dataTableRef = useRef<DataTableRef>(null);

  const handleDelete = useCallback(
    (row?: UnitOrder) => {
      setDeleteOrder(row);
      setEditOrder(undefined);
      setViewOrder(undefined);
      setImportOpen(false);
      openDialog();
    },
    [openDialog, setDeleteOrder, setEditOrder, setViewOrder]
  );

  const handleEdit = useCallback(
    (row?: UnitOrder) => {
      setDeleteOrder(undefined);
      setEditOrder(row);
      setViewOrder(undefined);
      setImportOpen(false);
      openDialog();
    },
    [openDialog, setDeleteOrder, setEditOrder, setViewOrder]
  );

  const handleView = useCallback(
    (...args: [string, any, UnitOrder]) => {
      setEditOrder(undefined);
      setDeleteOrder(undefined);
      setViewOrder(args[2]);
      setImportOpen(false);
      openDialog();
    },
    [openDialog, setDeleteOrder, setEditOrder, setViewOrder]
  );

  const handleImport = useCallback(() => {
    setDeleteOrder(undefined);
    setEditOrder(undefined);
    setViewOrder(undefined);
    setImportOpen(true);
    openDialog();
  }, [openDialog, setDeleteOrder, setEditOrder, setViewOrder]);

  const handleClose = useCallback(() => {
    setEditOrder(undefined);
    setDeleteOrder(undefined);
    setViewOrder(undefined);
    setImportOpen(false);
    closeDialog();
    dataTableRef.current?.forceFetch();
  }, [closeDialog, setDeleteOrder, setEditOrder, setViewOrder]);

  return (
    <div id="unit-orders" className="page d-flex">
      <div className="page-header">
        <h1>STOP / ALLOW ORDERS</h1>
        <div className="d-flex g-3">
          <button className="btn-outline" onClick={handleImport} data-cy="import-orders-button">
            import
          </button>
          <button className="btn-danger" onClick={() => navigate('/orders/new')} data-cy="new-stop-order-button">
            new stop order
          </button>
          <button
            className="btn-primary"
            onClick={() => navigate('/orders/new?type=allow')}
            data-cy="new-allow-order-button"
          >
            new allow order
          </button>
        </div>
      </div>
      <div className="page-content d-flex flex-column h-100">
        <DataTable
          ref={dataTableRef}
          columns={unitOrderColumns}
          fetchData={fetchUnitOrders}
          fetchCsv={downloadCsv('unit-orders')}
          initialColumns={unitOrderColumns.map(({ field, width }) => {
            return { field, width };
          })}
          initialPage={{ pageNum: 0, pageSize: 25, sortDirection: 'desc', sortField: 'createdDate' }}
          getRowActions={row => [
            <button className="btn-close" type="button" onClick={() => handleEdit(row)}>
              <span className="material-icons icon-large p-1">edit</span>
            </button>,
            <button className="btn-close" type="button" onClick={() => handleDelete(row)}>
              <span className="material-icons icon-large p-1">delete</span>
            </button>
          ]}
          onCellClick={handleView}
          showColumnsCategorized
        />
      </div>
      <Dialog closeDialog={handleClose} ref={ref} width={importOpen ? '960px' : '720px'}>
        {importOpen ? (
          <ImportOrders handleClose={handleClose} onImported={() => dataTableRef.current?.forceFetch()} />
        ) : deleteOrder ? (
          <DeleteUnitOrder order={deleteOrder} handleClose={handleClose} />
        ) : editOrder ? (
          <EditUnitOrder order={editOrder} handleClose={handleClose} />
        ) : (
          <div className="grid-2 mb-3">
            <h3 className="font-weight-medium text-info-dark">{!viewOrder?.allowed ? 'Stop' : 'Allow'} Order</h3>
            <div className="d-flex align-self-center justify-self-end g-2">
              <button className="btn-close" type="button" onClick={() => handleEdit(viewOrder)}>
                <span className="material-icons icon-large p-1">edit</span>
              </button>
              <button className="btn-close" type="button" onClick={() => handleDelete(viewOrder)}>
                <span className="material-icons icon-large p-1">delete</span>
              </button>
            </div>
            <dl>
              <dt className="text-big text-info-main">Account</dt>
              <dd className="text-big">{viewOrder?.customer?.name ?? 'N/A'}</dd>
            </dl>
            <dl>
              <dt className="text-big text-info-main">Unit Number</dt>
              <dd className="text-big">{viewOrder?.unitNumber ?? 'N/A'}</dd>
            </dl>
            <dl className="col-span-2">
              <dt className="text-big text-info-main">Notes</dt>
              <dd className="text-big">{viewOrder?.notes ?? 'N/A'}</dd>
            </dl>
          </div>
        )}
      </Dialog>
      <ErrorDialog />
      <SuccessDialog title="Orders Saved" />
    </div>
  );
};

export default UnitOrders;
